import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import useApi from '../../../../../../utils/AxiosWrapper';
import {
  CashAdvanceOptionsResponse,
  CashAdvanceOptionsStatus,
} from '../types/bankingTypes';

export const cashAdvanceOptionsQueryKey = (request_id: string) => [
  'cashAdvanceOptions',
  request_id,
];

export const useCashAdvanceOptionsQuery = (request_id: string | undefined) => {
  const { callProtectedApi } = useApi();

  return useQuery<CashAdvanceOptionsResponse | null>({
    queryKey: cashAdvanceOptionsQueryKey(request_id ?? ''),
    queryFn: async () => {
      const response = await callProtectedApi(
        `/credit/loan-request/${request_id ?? ''}`,
        'get',
        {},
        'Error occurred fetching your repayment options.'
      );
      const data = response?.data?.data?.loanRequest;
      if (!data) {
        return null;
      }
      return {
        status: data.status as CashAdvanceOptionsStatus,
        options_expire_at: data.options_expire_at ?? null,
        options_presented_at: data.options_presented_at ?? null,
        options_selected_at: data.options_selected_at ?? null,
        options_declined_at: data.options_declined_at ?? null,
        options_declined_reason: data.options_declined_reason ?? null,
        options_expired_at: data.options_expired_at ?? null,
        presentation_attempt: data.presentation_attempt ?? 0,
        options: data.options ?? [],
      };
    },
    enabled: !!request_id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    // Poll while the round is live so a deadline that passes while the page is
    // open flips to the expired state on its own, instead of leaving the
    // customer on a stale "active" view that only fails at the select 409.
    refetchInterval: (query) =>
      query.state.data?.status === CashAdvanceOptionsStatus.Active
        ? 60 * 1000
        : false,
  });
};

const TERMINAL_FIELD_MESSAGES: Record<string, string> = {
  options_selected_at: 'You have already selected a repayment plan.',
  options_declined_at: 'These options were already declined.',
  options_expire_at: 'These options have expired.',
  options_expired_at: 'These options have expired.',
};

export const useSelectCashAdvanceOption = (
  request_id: string | undefined,
  onSuccessNavigate: () => void
) => {
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (terms_id: string) => {
      const response = await callProtectedApi(
        `/credit/loan-request/${request_id ?? ''}/select-option`,
        'post',
        { terms_id },
        undefined,
        undefined,
        undefined,
        0,
        true
      );
      return response;
    },
    onSuccess: (response) => {
      const status = response?.status;
      if (status === 200) {
        queryClient.invalidateQueries({
          queryKey: cashAdvanceOptionsQueryKey(request_id ?? ''),
        });
        queryClient.invalidateQueries({ queryKey: ['loans'] });
        queryClient.invalidateQueries({
          queryKey: ['pendingConsentLoanRequests'],
        });
        onSuccessNavigate();
        return;
      }
      if (status === 409) {
        const field = response?.data?.data?.field as string | undefined;
        const message =
          (field && TERMINAL_FIELD_MESSAGES[field]) ||
          'These options are no longer available.';
        toast(message);
        queryClient.invalidateQueries({
          queryKey: cashAdvanceOptionsQueryKey(request_id ?? ''),
        });
        return;
      }
      if (status === 429) {
        toast('Another action is in progress. Please try again.');
        return;
      }
      toast.error(
        'Could not submit your selection. Please try again or contact support.'
      );
    },
  });
};

export const useDeclineCashAdvanceOptions = (
  request_id: string | undefined
) => {
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reason: string | undefined) => {
      const body = reason && reason.trim().length > 0 ? { reason } : {};
      const response = await callProtectedApi(
        `/credit/loan-request/${request_id ?? ''}/decline-options`,
        'post',
        body,
        undefined,
        undefined,
        undefined,
        0,
        true
      );
      return response;
    },
    onSuccess: (response) => {
      const status = response?.status;
      if (status === 200) {
        queryClient.invalidateQueries({
          queryKey: cashAdvanceOptionsQueryKey(request_id ?? ''),
        });
        queryClient.invalidateQueries({ queryKey: ['loans'] });
        queryClient.invalidateQueries({
          queryKey: ['pendingConsentLoanRequests'],
        });
        toast.success('Your response has been recorded.');
        return;
      }
      if (status === 409) {
        const field = response?.data?.data?.field as string | undefined;
        const message =
          (field && TERMINAL_FIELD_MESSAGES[field]) ||
          'These options are no longer available.';
        toast(message);
        queryClient.invalidateQueries({
          queryKey: cashAdvanceOptionsQueryKey(request_id ?? ''),
        });
        return;
      }
      if (status === 429) {
        toast('Another action is in progress. Please try again.');
        return;
      }
      toast.error(
        'Could not submit your response. Please try again or contact support.'
      );
    },
  });
};
